import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { getAuthMe, isApiConfigured, postAcceptLegal } from "../apiClient";
import { getLegalDoc, isLegalDocumentPath, legalDocsNeedingAcceptance } from "../legal/legalDocuments";

/**
 * Blocking re-consent gate. When a signed-in user has not accepted the current version of a legal
 * document (e.g. after we publish a material update), this overlays a modal asking them to agree
 * before continuing. New signups already accept at registration, so they won't see this.
 */
export default function LegalConsentGate({ currentUser, setCurrentUser, authToken }) {
  const { pathname } = useLocation();
  const [pendingTypes, setPendingTypes] = useState([]);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const userId = currentUser?.id || null;

  useEffect(() => {
    if (!userId || !authToken || !isApiConfigured()) {
      setPendingTypes([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { user } = await getAuthMe(authToken);
        if (cancelled) return;
        const accepted = user?.legal?.accepted || {};
        setPendingTypes(legalDocsNeedingAcceptance(accepted));
      } catch {
        if (!cancelled) setPendingTypes([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, authToken]);

  const handleAccept = useCallback(async () => {
    setError("");
    setSubmitting(true);
    try {
      const { legal } = await postAcceptLegal(authToken);
      setCurrentUser((prev) => (prev ? { ...prev, legal } : prev));
      setPendingTypes([]);
      setAgreed(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "We couldn't save that. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }, [authToken, setCurrentUser]);

  if (!userId || pendingTypes.length === 0) return null;
  if (isLegalDocumentPath(pathname)) return null;

  const docs = pendingTypes.map((type) => getLegalDoc(type)).filter(Boolean);

  return (
    <div className="jw-legal-gate-overlay" role="dialog" aria-modal="true" aria-labelledby="jw-legal-gate-title">
      <div className="jw-legal-gate-modal">
        <h2 id="jw-legal-gate-title">We&apos;ve updated our terms</h2>
        <p>
          We&apos;ve made some changes. Please take a moment to review and agree so you can carry on
          using JOBUWIN.
        </p>

        <label className="jw-legal-gate-consent">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(event) => {
              setAgreed(event.target.checked);
              if (event.target.checked) setError("");
            }}
          />
          <span>
            I agree to the{" "}
            {docs.map((doc, index) => (
              <span key={doc.type}>
                {index > 0 ? (index === docs.length - 1 ? " and " : ", ") : null}
                <Link to={doc.path}>
                  {doc.label}
                </Link>
              </span>
            ))}
            .
          </span>
        </label>

        {error ? (
          <p className="jw-legal-gate-error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="jw-legal-gate-actions">
          <button
            type="button"
            className="jw-public-button jw-public-button--primary"
            disabled={!agreed || submitting}
            onClick={handleAccept}
          >
            {submitting ? "Saving..." : "Agree and continue"}
          </button>
          <button
            type="button"
            className="jw-legal-gate-signout"
            disabled={submitting}
            onClick={() => setCurrentUser(null)}
          >
            Sign out instead
          </button>
        </div>
      </div>
    </div>
  );
}
